import React, { useCallback, useEffect, useState } from 'react'
import { Dialog, DialogContent, FormControl, IconButton, InputLabel, MenuItem, Select, Stack } from "@mui/material"
import { QrReader } from "react-qr-reader"

import {
  Close as CloseIcon
} from '@mui/icons-material'

import { defaultFunction } from "../../utilities/utilities"

import { useTranslation } from 'react-i18next'
import { DEFAUTL_QR_START_CODE, ID_CAMERA_QR } from "../../../variables"
import { connect } from 'react-redux'
import '../IDDialog/IDDialog.sass'

function QRDialog(props) {

  const {
    open = false,
    cameras = [],
    onClose = defaultFunction,
    onChangeUniqueIdentificator = defaultFunction,
    onInvalidQR = defaultFunction,
    message
  } = props

  const [camera, setCamera] = useState('');
  const [localCameras, setLocalCameras] = useState([]);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const { t } = useTranslation()

  const canEnumerateCameras = typeof navigator.mediaDevices?.enumerateDevices === 'function'

  const onQRResult = useCallback((result, _) => {
    if (!!result) {
      const { text = '' } = result
      if (text.startsWith(DEFAUTL_QR_START_CODE)) {
        onChangeUniqueIdentificator(text)
      } else {
        onInvalidQR()
      }
    }
  }, [onInvalidQR, onChangeUniqueIdentificator])

  const handleCameraChange = (event) => {
    localStorage.setItem(ID_CAMERA_QR, event.target.value)
    setCamera(event.target.value)
  }

  // Re-enumerate cameras each time the dialog opens, because labels are empty
  // until the browser has granted camera permission (which happens lazily via QrReader).
  useEffect(() => {
    if (!open) return;
    if (!canEnumerateCameras) { setCameraBlocked(true); return; }

    const enumerateLocalCameras = () => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          if (videoDevices.length > 0) setLocalCameras(videoDevices);
        })
        .catch(() => {});
    };

    enumerateLocalCameras();

    // Browsers fire devicechange when permission is granted and labels become available
    navigator.mediaDevices.addEventListener('devicechange', enumerateLocalCameras);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateLocalCameras);
    };
  }, [open, canEnumerateCameras]);

  // Sync from Redux when it already has data (e.g. permission was pre-granted)
  useEffect(() => {
    if (cameras.length > 0) setLocalCameras(cameras);
  }, [cameras]);

  // Poll while the list is empty or all labels are blank (permission not yet granted).
  // Self-stops once real labels are available — no need to close and reopen the dialog.
  useEffect(() => {
    if (!open || !canEnumerateCameras) return;
    if (localCameras.length > 0 && localCameras.some(c => c.label)) return;

    const timer = setTimeout(() => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          if (videoDevices.length > 0) setLocalCameras(videoDevices);
        })
        .catch(() => {});
    }, 1500);

    return () => clearTimeout(timer);
  }, [open, canEnumerateCameras, localCameras]);

  // Resolve the active camera: prefer a valid stored id, fall back to first available
  useEffect(() => {
    if (!open || localCameras.length === 0) return;
    const stored = localStorage.getItem(ID_CAMERA_QR);
    const isValid = localCameras.some(c => c.deviceId === stored);
    setCamera(isValid ? stored : localCameras[0].deviceId);
  }, [open, localCameras]);

  return (
    <Dialog
      className='visitor-dialog'
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="sm">
      <DialogContent>
        <Stack gap={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <h1>{t("Scan QR code")}</h1>
            <IconButton
              aria-label="close"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {cameraBlocked ? (
            <p style={{ color: 'var(--error, #d32f2f)', textAlign: 'center' }}>
              {t('Camera access requires a secure connection (HTTPS). Please contact your administrator.')}
            </p>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel id="select-camera">{t('Camera')}</InputLabel>
                <Select
                  labelId="select-camera"
                  id="demo-simple-select"
                  value={camera}
                  label={t("Camera")}
                  size='small'
                  onChange={handleCameraChange}
                >
                  {localCameras.map((cam) => <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Stack gap={2} alignItems="center">
                <div style={{
                  width: '399px',
                  position: 'relative',
                  overflow: 'hidden',
                  background: 'black',
                  border: 'solid 8px var(--primary)',
                  borderRadius: '5px',
                }}>
                  <QrReader
                    key={camera}
                    onResult={onQRResult}
                    style={{ width: '100%', height: '100%', }}
                    constraints={ camera ? { deviceId: { exact: camera } } : undefined }
                  />
                  <hr className="hr-animation" />
                </div>
                <h2>{t('Place the QR in the white rectangle.')}</h2>
              </Stack>
            </>
          )}
          {message}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

const mapStateToProps = state => {
  return {
    cameras: state.utilities.cameras 
  }
}

export default connect(mapStateToProps)(QRDialog)
